import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

export interface StatusOption {
  label: string;
  value: any;
  colorClass?: string;
  dotColor?: string;
}

@Component({
  selector: 'app-status-badge',
  templateUrl: './app-status-badge.component.html',
  styleUrls: ['./app-status-badge.component.scss'],
})
export class AppStatusBadgeComponent implements OnInit, OnChanges {
  @Input() status: string = 'Yet to start';
  @Input() label: string = ''; // e.g. 'ORDER STATUS'
  @Input() options: (string | StatusOption)[] = [
    'Yet to start',
    'In progress',
    'On hold',
    'Delivered'
  ];
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() showChevron: boolean = true;
  @Input() xPosition: 'before' | 'after' = 'after';
  @Input() yPosition: 'above' | 'below' = 'below';
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() customClass: string = '';
  @Input() menuClass: string = '';

  @Output() statusChange = new EventEmitter<string | any>();
  @Output() selectionChange = new EventEmitter<string | any>();
  @Output() badgeClick = new EventEmitter<MouseEvent>();

  cachedOptions: StatusOption[] = [];

  ngOnInit(): void {
    this.updateNormalizedOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.updateNormalizedOptions();
    }
  }

  get isInteractive(): boolean {
    return !this.disabled && !this.readonly;
  }

  get currentStatusDisplay(): string {
    return this.status || 'Yet to start';
  }

  get currentStatusClass(): string {
    return this.resolveStatusClass(this.status);
  }

  private updateNormalizedOptions(): void {
    const list = this.options && this.options.length > 0
      ? this.options
      : ['Yet to start', 'In progress', 'On hold', 'Delivered'];

    this.cachedOptions = list.map(opt => {
      if (typeof opt === 'string') {
        return {
          label: opt,
          value: opt,
          colorClass: this.resolveStatusClass(opt),
          dotColor: this.resolveDotColor(opt)
        };
      }
      return {
        ...opt,
        colorClass: opt.colorClass || this.resolveStatusClass(opt.label || String(opt.value)),
        dotColor: opt.dotColor || this.resolveDotColor(opt.label || String(opt.value))
      };
    });
  }

  resolveStatusClass(st: string): string {
    const s = (st || '').trim().toLowerCase();
    if (s === 'yet to start' || s === 'yet-start' || s === 'draft') return 'yet-start';
    if (s === 'in progress' || s === 'in-progress' || s === 'yet-progress' || s === 'processing' || s === 'sent') return 'yet-progress';
    if (s === 'on hold' || s === 'on-hold' || s === 'partially paid' || s === 'pending') return 'on-hold';
    if (s === 'delivered' || s === 'paid' || s === 'completed' || s === 'active' || s === 'approved') return 'delivered';
    if (s === 'cancelled' || s === 'rejected' || s === 'declined' || s === 'overdue') return 'cancelled';
    return 'yet-start';
  }

  resolveDotColor(st: string): string {
    const s = (st || '').trim().toLowerCase();
    if (s === 'yet to start' || s === 'yet-start' || s === 'draft') return '#909090';
    if (s === 'in progress' || s === 'in-progress' || s === 'yet-progress' || s === 'processing' || s === 'sent') return '#3197ff';
    if (s === 'on hold' || s === 'on-hold' || s === 'partially paid' || s === 'pending') return '#ffa34d';
    if (s === 'delivered' || s === 'paid' || s === 'completed' || s === 'active' || s === 'approved') return '#00BA5C';
    if (s === 'cancelled' || s === 'rejected' || s === 'declined' || s === 'overdue') return '#f64e60';
    return '#909090';
  }

  onSelect(opt: StatusOption): void {
    this.status = opt.value;
    this.statusChange.emit(opt.value);
    this.selectionChange.emit(opt);
  }

  onBadgeClick(event: MouseEvent): void {
    if (!this.isInteractive) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    this.badgeClick.emit(event);
  }

  trackByOption(_index: number, item: StatusOption): any {
    return item.value || item.label;
  }
}

